import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { StateManager } from '../../state/StateManager';
import type { StateDebugSnapshot } from '../../state/StateManager';
import type { CharacterDebugSnapshot, ProjectileDebugSnapshot } from '../../state/types';

/**
 * State Observer Panel
 * 
 * Sprint 0023: Unified State Observability System
 * 
 * Shows live state machine visualization:
 * - Character states (locomotion, combat, health)
 * - Projectile states (flight, detonation)
 * - State transitions and event history
 */
@customElement('state-observer-panel')
export class StateObserverPanel extends LitElement {
  static styles = css`
    :host {
      display: block;
      height: 100%;
      color: #eee;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 12px;
    }

    .panel-content {
      display: flex;
      flex-direction: column;
      height: 100%;
      padding: 12px;
      gap: 12px;
      overflow-y: auto;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
    }

    .header-title {
      font-weight: 600;
      font-size: 13px;
    }

    .subscription-indicator {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 10px;
      opacity: 0.7;
    }

    .indicator-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #666;
    }

    .indicator-dot.subscribed {
      background: #6f6;
      animation: pulse 1.5s ease-in-out infinite;
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }

    .section {
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 4px;
      padding: 8px;
      background: rgba(0, 0, 0, 0.2);
    }

    .section-title {
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      opacity: 0.75;
      margin-bottom: 8px;
      padding-bottom: 6px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .count-badge {
      background: rgba(255, 255, 255, 0.15);
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 10px;
    }

    .character-card {
      padding: 8px;
      margin-bottom: 6px;
      border-radius: 4px;
      background: rgba(255, 255, 255, 0.05);
    }

    .character-card:last-child {
      margin-bottom: 0;
    }

    .character-id {
      font-weight: 600;
      color: #9cf;
      margin-bottom: 6px;
    }

    .state-row {
      display: flex;
      gap: 8px;
      margin-bottom: 4px;
      align-items: center;
    }

    .state-label {
      min-width: 70px;
      opacity: 0.7;
      font-size: 11px;
    }

    .state-value {
      padding: 2px 8px;
      border-radius: 3px;
      font-size: 11px;
      font-weight: 500;
    }

    .state-locomotion {
      background: rgba(100, 180, 255, 0.2);
      color: #6bf;
    }

    .state-combat {
      background: rgba(255, 150, 100, 0.2);
      color: #f96;
    }

    .state-health {
      background: rgba(100, 255, 150, 0.2);
      color: #6f9;
    }

    .state-health.dead {
      background: rgba(255, 100, 100, 0.3);
      color: #f66;
    }

    .health-bar {
      flex: 1;
      height: 6px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 3px;
      overflow: hidden;
    }

    .health-bar-fill {
      height: 100%;
      background: linear-gradient(90deg, #4f4, #6f6);
      transition: width 0.2s;
    }

    .health-bar-fill.low {
      background: linear-gradient(90deg, #f44, #f66);
    }

    .attack-info {
      margin-top: 6px;
      padding-top: 6px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      font-size: 11px;
    }

    .attack-phase {
      display: inline-block;
      padding: 2px 6px;
      border-radius: 3px;
      margin-left: 6px;
      font-weight: 500;
    }

    .phase-windup { background: rgba(255, 200, 100, 0.2); color: #fc9; }
    .phase-active { background: rgba(255, 100, 100, 0.2); color: #f66; }
    .phase-recovery { background: rgba(100, 200, 255, 0.2); color: #6cf; }

    .projectile-card {
      padding: 6px 8px;
      margin-bottom: 4px;
      border-radius: 4px;
      background: rgba(255, 255, 255, 0.05);
      font-size: 11px;
    }

    .projectile-id {
      font-weight: 500;
      color: #fc9;
    }

    .empty-state {
      text-align: center;
      padding: 20px;
      opacity: 0.5;
      font-style: italic;
    }

    .transition-item {
      display: flex;
      gap: 8px;
      padding: 4px 0;
      font-size: 10px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      align-items: center;
    }

    .transition-item:last-child {
      border-bottom: none;
    }

    .transition-time {
      color: #888;
      min-width: 50px;
    }

    .transition-char {
      color: #9cf;
      min-width: 80px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .transition-arrow {
      color: #6f6;
    }

    .transition-event {
      color: #fc9;
      font-family: monospace;
      font-size: 9px;
    }

    .transition-list {
      max-height: 150px;
      overflow-y: auto;
    }
  `;

  @state() private snapshot: StateDebugSnapshot | null = null;
  @state() private isSubscribed = false;

  private unsubscribe: (() => void) | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.subscribe();
  }

  disconnectedCallback(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.isSubscribed = false;
    super.disconnectedCallback();
  }

  private subscribe(): void {
    const stateManager = StateManager.getInstance();
    this.unsubscribe = stateManager.subscribeDebug((snapshot) => {
      this.snapshot = snapshot;
    });
    this.isSubscribed = true;
  }

  render() {
    return html`
      <div class="panel-content">
        <div class="header">
          <span class="header-title">State Machines (XState)</span>
          <div class="subscription-indicator">
            <div class="indicator-dot ${this.isSubscribed ? 'subscribed' : ''}"></div>
            ${this.isSubscribed ? 'Live' : 'Disconnected'}
          </div>
        </div>

        ${this.renderCharacters()}
        ${this.renderTransitionHistory()}
        ${this.renderProjectiles()}
      </div>
    `;
  }

  private renderCharacters() {
    const characters = this.snapshot?.characters ?? [];

    return html`
      <div class="section">
        <div class="section-title">
          Characters
          <span class="count-badge">${characters.length}</span>
        </div>
        ${characters.length === 0
          ? html`<div class="empty-state">No character actors</div>`
          : characters.map(char => this.renderCharacterCard(char))}
      </div>
    `;
  }

  private renderCharacterCard(char: CharacterDebugSnapshot) {
    // Note: CharacterDebugSnapshot uses flat properties, not nested context/stateValue
    const healthPercent = 100; // TODO: need maxHealth in snapshot
    const isLow = char.health < 30;
    const isDead = char.healthState === 'dead';

    return html`
      <div class="character-card">
        <div class="character-id">${char.characterId}</div>
        
        <div class="state-row">
          <span class="state-label">Locomotion</span>
          <span class="state-value state-locomotion">${char.locomotionState}</span>
        </div>
        
        <div class="state-row">
          <span class="state-label">Combat</span>
          <span class="state-value state-combat">${this.formatCombatState(char.combatState)}</span>
        </div>
        
        <div class="state-row">
          <span class="state-label">Health</span>
          <span class="state-value state-health ${isDead ? 'dead' : ''}">${char.healthState}</span>
          <span style="margin-left: 8px; opacity: 0.7;">${char.health} HP</span>
          <div class="health-bar">
            <div 
              class="health-bar-fill ${isLow ? 'low' : ''}" 
              style="width: ${healthPercent}%">
            </div>
          </div>
        </div>

        ${char.attackId ? this.renderAttackInfo(char) : null}
      </div>
    `;
  }

  private formatCombatState(combat: unknown): string {
    if (typeof combat === 'string') return combat;
    if (typeof combat === 'object' && combat !== null) {
      // Handle nested states like { attacking: 'active' }
      const entries = Object.entries(combat);
      if (entries.length > 0) {
        const [parent, child] = entries[0];
        return `${parent}:${child}`;
      }
    }
    return String(combat);
  }

  private renderAttackInfo(char: CharacterDebugSnapshot) {
    const attack = char.attackId;
    if (!attack) return null;

    const phase = char.attackPhase ?? 'unknown';
    const progress = char.attackNormalizedTime ?? 0;

    return html`
      <div class="attack-info">
        <strong>Attack:</strong> ${attack}
        <span class="attack-phase phase-${phase}">${phase} (${Math.round(progress * 100)}%)</span>
      </div>
    `;
  }

  private renderProjectiles() {
    const projectiles = this.snapshot?.projectiles ?? [];

    return html`
      <div class="section">
        <div class="section-title">
          Projectiles
          <span class="count-badge">${projectiles.length}</span>
        </div>
        ${projectiles.length === 0
          ? html`<div class="empty-state">No active projectiles</div>`
          : projectiles.map(proj => this.renderProjectileCard(proj))}
      </div>
    `;
  }

  private renderProjectileCard(proj: ProjectileDebugSnapshot) {
    return html`
      <div class="projectile-card">
        <span class="projectile-id">${proj.projectileId}</span>
        <span style="opacity: 0.6; margin-left: 8px;">
          flight: ${proj.flightState} | 
          detonation: ${proj.detonationState}
        </span>
      </div>
    `;
  }

  private renderTransitionHistory() {
    const transitions = this.snapshot?.recentTransitions ?? [];
    
    // Show most recent first
    const reversed = [...transitions].reverse();

    return html`
      <div class="section">
        <div class="section-title">
          Transitions
          <span class="count-badge">${transitions.length}</span>
        </div>
        <div class="transition-list">
          ${reversed.length === 0
            ? html`<div class="empty-state">No transitions yet</div>`
            : reversed.map(t => this.renderTransitionItem(t))}
        </div>
      </div>
    `;
  }

  private renderTransitionItem(t: { characterId: string; timestamp: number; from: string; to: string; event: string }) {
    const ageMs = performance.now() - t.timestamp;
    const ageStr = ageMs < 1000 ? `${Math.round(ageMs)}ms` : `${(ageMs / 1000).toFixed(1)}s`;
    
    // Shorten character ID for display
    const shortId = t.characterId.length > 12 ? t.characterId.slice(0, 10) + '…' : t.characterId;

    return html`
      <div class="transition-item">
        <span class="transition-time">${ageStr} ago</span>
        <span class="transition-char">${shortId}</span>
        <span>${t.from}</span>
        <span class="transition-arrow">→</span>
        <span>${t.to}</span>
        <span class="transition-event">(${t.event})</span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'state-observer-panel': StateObserverPanel;
  }
}
